import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Heart, Star, Settings, Building2, Plus, Edit, Trash2,
  Eye, BarChart3, Phone, Navigation, ExternalLink, Search, Upload,
  MapPin, Globe, Clock, Save, X, ChevronRight, FileText, Import,
  User, Mail, DollarSign, Facebook, Twitter, Instagram, Linkedin,
  TrendingUp, Calendar, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ListingCard from '../components/common/ListingCard';
import { useAuth } from '../context/AuthContext';
import { userAPI, analyticsAPI, listingsAPI, seoAPI, specialtiesAPI } from '../lib/api';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [myListings, setMyListings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit listing state
  const [editingListing, setEditingListing] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [editTab, setEditTab] = useState('basic');
  
  // SEO Editor state
  const [seoListing, setSeoListing] = useState(null);
  const [seoData, setSeoData] = useState({});
  const [savingSeo, setSavingSeo] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  
  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importMethod, setImportMethod] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);

  // Temp inputs for array fields
  const [tempInputs, setTempInputs] = useState({
    category: '',
    specialty: '',
    service: '',
    insurance: '',
    language: '',
    photoUrl: '',
  });

  useEffect(() => {
    loadData();
    loadSpecialties();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listingsRes, favoritesRes, reviewsRes] = await Promise.all([
        userAPI.getListings(),
        userAPI.getFavorites(),
        userAPI.getReviews(),
      ]);
      
      setMyListings(listingsRes.data);
      setFavorites(favoritesRes.data);
      setReviews(reviewsRes.data);

      // Load analytics for each listing
      const analyticsPromises = listingsRes.data.map(async (listing) => {
        try {
          const res = await analyticsAPI.getListingAnalytics(listing.listing_id);
          return { [listing.listing_id]: res.data };
        } catch (e) {
          return { [listing.listing_id]: { views: 0, clicks: 0, calls: 0, directions: 0 } };
        }
      });
      
      const analyticsResults = await Promise.all(analyticsPromises);
      const analyticsMap = analyticsResults.reduce((acc, item) => ({ ...acc, ...item }), {});
      setAnalytics(analyticsMap);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSpecialties = async () => {
    try {
      const res = await specialtiesAPI.getAll();
      setSpecialties(res.data);
    } catch (error) {
      console.error('Error loading specialties:', error);
    }
  };

  // Edit Listing Functions
  const handleEditListing = (listing) => {
    setEditingListing(listing);
    setEditTab('basic');
    setEditFormData({
      name: listing.name || '',
      contact_name: listing.contact_name || '',
      registration_number: listing.registration_number || '',
      description: listing.description || '',
      street: listing.street || '',
      city: listing.city || '',
      state: listing.state || '',
      zip_code: listing.zip_code || '',
      region: listing.region || '',
      phone: listing.phone || '',
      email: listing.email || '',
      website: listing.website || '',
      booking_url: listing.booking_url || '',
      categories: listing.categories || [],
      specialties: listing.specialties || [],
      services: listing.services || [],
      insurance_accepted: listing.insurance_accepted || [],
      languages: listing.languages || ['English'],
      price_range: listing.price_range || '',
      logo: listing.logo || '',
      photos: listing.photos || [],
      social_networks: listing.social_networks || {
        facebook: '',
        twitter: '',
        instagram: '',
        linkedin: '',
      },
    });
  };

  const handleSaveListing = async () => {
    if (!editingListing) return;
    
    setSaving(true);
    try {
      await listingsAPI.update(editingListing.listing_id, editFormData);
      toast.success('Listing updated!');
      setEditingListing(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await listingsAPI.delete(listingId);
      toast.success('Listing deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  // Helper functions for array fields
  const addToEditArray = (field, value) => {
    if (value && !editFormData[field].includes(value)) {
      setEditFormData(prev => ({ ...prev, [field]: [...prev[field], value] }));
    }
    const inputKey = field === 'insurance_accepted' ? 'insurance' : 
                     field === 'specialties' ? 'specialty' :
                     field === 'categories' ? 'category' :
                     field === 'photos' ? 'photoUrl' : field.slice(0, -1);
    setTempInputs(prev => ({ ...prev, [inputKey]: '' }));
  };

  const removeFromEditArray = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: prev[field].filter(v => v !== value) }));
  };

  const updateSocialNetwork = (network, value) => {
    setEditFormData(prev => ({
      ...prev,
      social_networks: { ...prev.social_networks, [network]: value },
    }));
  };

  // SEO Functions
  const handleEditSeo = async (listing) => {
    setSeoListing(listing);
    try {
      const res = await seoAPI.get('listing', listing.listing_id);
      const seo = res.data.seo || {};
      setSeoData({
        meta_title: seo.meta_title || '',
        meta_description: seo.meta_description || '',
        focus_keywords: seo.focus_keywords || [],
        excerpt: seo.excerpt || '',
        og_title: seo.og_title || '',
        og_description: seo.og_description || '',
        og_image: seo.og_image || '',
        canonical_url: seo.canonical_url || '',
        no_index: seo.no_index || false,
        schema_type: seo.schema_type || '',
      });
    } catch (error) {
      console.error('Error loading SEO:', error);
    }
  };

  const handleSaveSeo = async () => {
    if (!seoListing) return;
    
    setSavingSeo(true);
    try {
      await seoAPI.update('listing', seoListing.listing_id, seoData);
      toast.success('SEO settings saved!');
      setSeoListing(null);
    } catch (error) {
      toast.error('Failed to save SEO settings');
    } finally {
      setSavingSeo(false);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !seoData.focus_keywords?.includes(keywordInput.trim())) {
      setSeoData({
        ...seoData,
        focus_keywords: [...(seoData.focus_keywords || []), keywordInput.trim()],
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setSeoData({
      ...seoData,
      focus_keywords: seoData.focus_keywords?.filter((k) => k !== keyword) || [],
    });
  };

  // Import Functions
  const handleGoogleImport = () => {
    toast.info('Google Business import requires Google My Business API setup. Please use URL import or manual form for now.');
  };

  const handleUrlImport = async () => {
    if (!importUrl.trim()) {
      toast.error('Please enter a Google Business URL');
      return;
    }
    
    setImporting(true);
    try {
      const urlPattern = /google\.com\/maps\/place\/([^\/]+)/;
      const match = importUrl.match(urlPattern);
      
      if (match) {
        const businessName = decodeURIComponent(match[1].replace(/\+/g, ' '));
        toast.success(`Business name extracted: ${businessName}`);
        navigate(`/add-listing?name=${encodeURIComponent(businessName)}&from=import`);
      } else {
        toast.error('Could not parse Google Business URL. Please use the manual form.');
      }
    } catch (error) {
      toast.error('Failed to import from URL');
    } finally {
      setImporting(false);
      setShowImportDialog(false);
    }
  };

  const totalViews = Object.values(analytics).reduce((sum, a) => sum + (a.views || 0), 0);
  const totalCalls = Object.values(analytics).reduce((sum, a) => sum + (a.calls || 0), 0);
  const totalDirections = Object.values(analytics).reduce((sum, a) => sum + (a.directions || 0), 0);
  
  // Calculate max values for chart scaling
  const maxViews = Math.max(...Object.values(analytics).map(a => a.views || 0), 1);
  const maxCalls = Math.max(...Object.values(analytics).map(a => a.calls || 0), 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="user-dashboard">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Welcome back, {user?.name || 'User'}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowImportDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Import Business
            </Button>
            <Link to="/add-listing">
              <Button className="bg-teal-700 hover:bg-teal-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Listing
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-teal-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{myListings.length}</p>
                  <p className="text-sm text-gray-600">My Listings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{totalViews}</p>
                  <p className="text-sm text-gray-600">Total Views</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-green-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{totalCalls}</p>
                  <p className="text-sm text-gray-600">Phone Calls</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Navigation className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{totalDirections}</p>
                  <p className="text-sm text-gray-600">Directions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Charts Section */}
        {myListings.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Views Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Views by Listing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {myListings.slice(0, 5).map((listing) => {
                    const views = analytics[listing.listing_id]?.views || 0;
                    const percentage = (views / maxViews) * 100;
                    return (
                      <div key={listing.listing_id}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="truncate max-w-[200px] text-gray-700">{listing.name}</span>
                          <span className="font-medium text-gray-900">{views}</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Calls & Directions Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                  Calls & Directions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {myListings.slice(0, 5).map((listing) => {
                    const calls = analytics[listing.listing_id]?.calls || 0;
                    const directions = analytics[listing.listing_id]?.directions || 0;
                    const maxVal = Math.max(maxCalls, 1);
                    return (
                      <div key={listing.listing_id}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="truncate max-w-[200px] text-gray-700">{listing.name}</span>
                          <div className="flex gap-3">
                            <span className="text-green-600 font-medium">{calls} calls</span>
                            <span className="text-purple-600 font-medium">{directions} dirs</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                              style={{ width: `${(calls / maxVal) * 100}%` }}
                            />
                          </div>
                          <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-500"
                              style={{ width: `${(directions / maxVal) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-gray-600">Calls</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span className="text-gray-600">Directions</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList>
            <TabsTrigger value="listings" data-testid="listings-tab">
              <Building2 className="w-4 h-4 mr-2" />
              My Listings
            </TabsTrigger>
            <TabsTrigger value="favorites" data-testid="favorites-tab">
              <Heart className="w-4 h-4 mr-2" />
              Favorites
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="reviews-tab">
              <Star className="w-4 h-4 mr-2" />
              My Reviews
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            {myListings.length > 0 ? (
              <div className="space-y-4">
                {myListings.map((listing) => (
                  <Card key={listing.listing_id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <img
                            src={listing.logo || listing.photos?.[0] || 'https://images.pexels.com/photos/127873/pexels-photo-127873.jpeg'}
                            alt={listing.name}
                            className="w-24 h-24 rounded-lg object-cover"
                          />
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading font-semibold text-lg text-gray-900">
                                {listing.name}
                              </h3>
                              <Badge className={
                                listing.status === 'approved' ? 'bg-green-100 text-green-700' :
                                listing.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                              }>
                                {listing.status}
                              </Badge>
                              <Badge className={`badge-plan ${listing.plan}`}>
                                {listing.plan}
                              </Badge>
                            </div>
                            <p className="text-gray-600 text-sm mb-2">
                              {listing.city}, {listing.state}
                              {listing.region && ` • ${listing.region}`}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                {analytics[listing.listing_id]?.views || 0} views
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                {analytics[listing.listing_id]?.calls || 0} calls
                              </span>
                              <span className="flex items-center gap-1">
                                <Star className="w-4 h-4" />
                                {listing.rating_avg?.toFixed(1) || '0.0'} ({listing.review_count || 0})
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Link to={`/listing/${listing.listing_id}`}>
                            <Button variant="outline" size="sm" title="View">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            title="Edit"
                            onClick={() => handleEditListing(listing)}
                            data-testid={`edit-listing-${listing.listing_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            title="SEO Settings"
                            onClick={() => handleEditSeo(listing)}
                            data-testid={`seo-listing-${listing.listing_id}`}
                          >
                            <Search className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            title="Delete"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteListing(listing.listing_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {listing.plan === 'free' && (
                            <Link to="/pricing">
                              <Button size="sm" className="bg-teal-700 hover:bg-teal-800">
                                Upgrade
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">No listings yet</h3>
                  <p className="text-gray-600 mb-4">Create your first listing to get started</p>
                  <div className="flex gap-4 justify-center">
                    <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Business
                    </Button>
                    <Link to="/add-listing">
                      <Button className="bg-teal-700 hover:bg-teal-800">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Listing
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="favorites">
            {favorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((listing) => (
                  <ListingCard key={listing.listing_id} listing={listing} isFavorite />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">No favorites yet</h3>
                  <p className="text-gray-600 mb-4">Save listings you're interested in</p>
                  <Link to="/search">
                    <Button variant="outline">Browse Listings</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="reviews">
            {reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card key={review.review_id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-600">{review.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">No reviews yet</h3>
                  <p className="text-gray-600 mb-4">Share your experiences with healthcare providers</p>
                  <Link to="/search">
                    <Button variant="outline">Find a Provider</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Enhanced Edit Listing Dialog */}
      <Dialog open={!!editingListing} onOpenChange={() => setEditingListing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Listing</DialogTitle>
          </DialogHeader>
          
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b mb-4">
            {[
              { id: 'basic', label: 'Basic Info', icon: Building2 },
              { id: 'contact', label: 'Contact', icon: Phone },
              { id: 'details', label: 'Details', icon: FileText },
              { id: 'media', label: 'Media & Social', icon: ImageIcon },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setEditTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  editTab === tab.id
                    ? 'border-teal-700 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="space-y-4 py-2">
            {/* Basic Info Tab */}
            {editTab === 'basic' && (
              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    Business Name *
                  </Label>
                  <Input
                    value={editFormData.name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    Contact Name
                  </Label>
                  <Input
                    value={editFormData.contact_name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, contact_name: e.target.value })}
                    placeholder="Primary contact person"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    Registration Number
                  </Label>
                  <Input
                    value={editFormData.registration_number || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, registration_number: e.target.value })}
                    placeholder="Business license / registration #"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {editTab === 'contact' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-500" />
                      Website
                    </Label>
                    <Input
                      value={editFormData.website || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                      placeholder="https://"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      Email
                    </Label>
                    <Input
                      value={editFormData.email || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      Phone
                    </Label>
                    <Input
                      value={editFormData.phone || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Booking URL</Label>
                    <Input
                      value={editFormData.booking_url || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, booking_url: e.target.value })}
                      placeholder="https://booking.example.com"
                    />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    Street Address
                  </Label>
                  <Input
                    value={editFormData.street || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, street: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label>City</Label>
                    <Input
                      value={editFormData.city || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={editFormData.state || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input
                      value={editFormData.zip_code || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, zip_code: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Region / Area</Label>
                  <Input
                    value={editFormData.region || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, region: e.target.value })}
                    placeholder="e.g., Manhattan, Downtown"
                  />
                </div>
              </div>
            )}

            {/* Details Tab */}
            {editTab === 'details' && (
              <div className="space-y-4">
                {/* Categories */}
                <div>
                  <Label>Categories / Facility Type</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Add category"
                      value={tempInputs.category}
                      onChange={(e) => setTempInputs(prev => ({ ...prev, category: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToEditArray('categories', tempInputs.category))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToEditArray('categories', tempInputs.category)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {editFormData.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.categories.map(c => (
                        <Badge key={c} variant="secondary" className="pl-3">
                          {c}
                          <button onClick={() => removeFromEditArray('categories', c)} className="ml-2"><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Specialties */}
                <div>
                  <Label>Specialties</Label>
                  <Select onValueChange={(v) => addToEditArray('specialties', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map(s => (
                        <SelectItem key={s.specialty_id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editFormData.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.specialties.map(s => (
                        <Badge key={s} variant="secondary" className="pl-3">
                          {s}
                          <button onClick={() => removeFromEditArray('specialties', s)} className="ml-2"><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Services */}
                <div>
                  <Label>Services</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Add service"
                      value={tempInputs.service}
                      onChange={(e) => setTempInputs(prev => ({ ...prev, service: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToEditArray('services', tempInputs.service))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToEditArray('services', tempInputs.service)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {editFormData.services?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.services.map(s => (
                        <Badge key={s} variant="outline" className="pl-3">
                          {s}
                          <button onClick={() => removeFromEditArray('services', s)} className="ml-2"><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Insurance */}
                <div>
                  <Label>Insurance Accepted</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Add insurance"
                      value={tempInputs.insurance}
                      onChange={(e) => setTempInputs(prev => ({ ...prev, insurance: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToEditArray('insurance_accepted', tempInputs.insurance))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToEditArray('insurance_accepted', tempInputs.insurance)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {editFormData.insurance_accepted?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.insurance_accepted.map(i => (
                        <Badge key={i} variant="outline" className="pl-3">
                          {i}
                          <button onClick={() => removeFromEditArray('insurance_accepted', i)} className="ml-2"><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Languages */}
                <div>
                  <Label>Languages</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Add language"
                      value={tempInputs.language}
                      onChange={(e) => setTempInputs(prev => ({ ...prev, language: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToEditArray('languages', tempInputs.language))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToEditArray('languages', tempInputs.language)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {editFormData.languages?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editFormData.languages.map(l => (
                        <Badge key={l} variant="secondary" className="pl-3">
                          {l}
                          <button onClick={() => removeFromEditArray('languages', l)} className="ml-2"><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Range */}
                <div>
                  <Label className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-500" />
                    Price Range
                  </Label>
                  <Select
                    value={editFormData.price_range || 'none'}
                    onValueChange={(v) => setEditFormData({ ...editFormData, price_range: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select price range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      <SelectItem value="$">$ - Budget Friendly</SelectItem>
                      <SelectItem value="$$">$$ - Moderate</SelectItem>
                      <SelectItem value="$$$">$$$ - Higher End</SelectItem>
                      <SelectItem value="$$$$">$$$$ - Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Media & Social Tab */}
            {editTab === 'media' && (
              <div className="space-y-4">
                {/* Logo */}
                <div>
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-gray-500" />
                    Logo URL
                  </Label>
                  <Input
                    value={editFormData.logo || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, logo: e.target.value })}
                    placeholder="https://yoursite.com/logo.png"
                  />
                  {editFormData.logo && (
                    <div className="mt-2">
                      <img 
                        src={editFormData.logo} 
                        alt="Logo preview" 
                        className="h-16 w-16 object-contain rounded border"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                {/* Gallery */}
                <div>
                  <Label>Gallery Images</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Add image URL"
                      value={tempInputs.photoUrl}
                      onChange={(e) => setTempInputs(prev => ({ ...prev, photoUrl: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToEditArray('photos', tempInputs.photoUrl))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToEditArray('photos', tempInputs.photoUrl)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {editFormData.photos?.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {editFormData.photos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={photo} 
                            alt={`Gallery ${idx + 1}`}
                            className="w-full h-20 object-cover rounded border"
                          />
                          <button
                            onClick={() => removeFromEditArray('photos', photo)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Social Networks */}
                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Social Networks</Label>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="flex items-center gap-2 text-sm text-gray-600">
                        <Facebook className="w-4 h-4 text-blue-600" />
                        Facebook
                      </Label>
                      <Input
                        value={editFormData.social_networks?.facebook || ''}
                        onChange={(e) => updateSocialNetwork('facebook', e.target.value)}
                        placeholder="https://facebook.com/yourpage"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2 text-sm text-gray-600">
                        <Twitter className="w-4 h-4 text-sky-500" />
                        Twitter / X
                      </Label>
                      <Input
                        value={editFormData.social_networks?.twitter || ''}
                        onChange={(e) => updateSocialNetwork('twitter', e.target.value)}
                        placeholder="https://twitter.com/yourhandle"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2 text-sm text-gray-600">
                        <Instagram className="w-4 h-4 text-pink-600" />
                        Instagram
                      </Label>
                      <Input
                        value={editFormData.social_networks?.instagram || ''}
                        onChange={(e) => updateSocialNetwork('instagram', e.target.value)}
                        placeholder="https://instagram.com/yourhandle"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2 text-sm text-gray-600">
                        <Linkedin className="w-4 h-4 text-blue-700" />
                        LinkedIn
                      </Label>
                      <Input
                        value={editFormData.social_networks?.linkedin || ''}
                        onChange={(e) => updateSocialNetwork('linkedin', e.target.value)}
                        placeholder="https://linkedin.com/company/yourcompany"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingListing(null)}>Cancel</Button>
            <Button onClick={handleSaveListing} disabled={saving} className="bg-teal-700 hover:bg-teal-800">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEO Editor Dialog */}
      <Dialog open={!!seoListing} onOpenChange={() => setSeoListing(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>SEO Settings - {seoListing?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Meta Title</Label>
              <Input
                placeholder="Page title for search engines (50-60 characters)"
                value={seoData.meta_title || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_title: e.target.value })}
                maxLength={70}
              />
              <p className="text-xs text-gray-500 mt-1">
                {(seoData.meta_title || '').length}/60 characters
              </p>
            </div>

            <div>
              <Label>Meta Description</Label>
              <Textarea
                placeholder="Brief description for search results (150-160 characters)"
                value={seoData.meta_description || ''}
                onChange={(e) => setSeoData({ ...seoData, meta_description: e.target.value })}
                rows={3}
                maxLength={200}
              />
              <p className="text-xs text-gray-500 mt-1">
                {(seoData.meta_description || '').length}/160 characters
              </p>
            </div>

            <div>
              <Label>Focus Keywords</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Add keyword"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <Button type="button" onClick={addKeyword} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {seoData.focus_keywords?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {seoData.focus_keywords.map((keyword) => (
                    <Badge key={keyword} variant="secondary" className="pl-3">
                      {keyword}
                      <button onClick={() => removeKeyword(keyword)} className="ml-2 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Excerpt / Summary</Label>
              <Textarea
                placeholder="Short summary for previews"
                value={seoData.excerpt || ''}
                onChange={(e) => setSeoData({ ...seoData, excerpt: e.target.value })}
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Open Graph (Social Sharing)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>OG Title</Label>
                  <Input
                    value={seoData.og_title || ''}
                    onChange={(e) => setSeoData({ ...seoData, og_title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>OG Image URL</Label>
                  <Input
                    value={seoData.og_image || ''}
                    onChange={(e) => setSeoData({ ...seoData, og_image: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>OG Description</Label>
                <Textarea
                  value={seoData.og_description || ''}
                  onChange={(e) => setSeoData({ ...seoData, og_description: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Advanced Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Canonical URL</Label>
                  <Input
                    value={seoData.canonical_url || ''}
                    onChange={(e) => setSeoData({ ...seoData, canonical_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Schema Type</Label>
                  <Select
                    value={seoData.schema_type || 'none'}
                    onValueChange={(v) => setSeoData({ ...seoData, schema_type: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="MedicalClinic">MedicalClinic</SelectItem>
                      <SelectItem value="Hospital">Hospital</SelectItem>
                      <SelectItem value="Physician">Physician</SelectItem>
                      <SelectItem value="Pharmacy">Pharmacy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Search Preview</h3>
              <div className="bg-white border rounded-lg p-4">
                <p className="text-blue-600 text-lg hover:underline">
                  {seoData.meta_title || seoListing?.name || 'Page Title'}
                </p>
                <p className="text-green-700 text-sm">
                  medsearcher.com/listing/{seoListing?.listing_id}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {seoData.meta_description || 'Add a meta description...'}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeoListing(null)}>Cancel</Button>
            <Button onClick={handleSaveSeo} disabled={savingSeo} className="bg-teal-700 hover:bg-teal-800">
              {savingSeo ? 'Saving...' : 'Save SEO Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Business Listing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!importMethod ? (
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-4"
                  onClick={() => setImportMethod('google')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">Connect Google Account</p>
                      <p className="text-sm text-gray-500">Import from Google Business Profile</p>
                    </div>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-4"
                  onClick={() => setImportMethod('url')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <ExternalLink className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">Import by URL</p>
                      <p className="text-sm text-gray-500">Paste your Google Business URL</p>
                    </div>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-4"
                  onClick={() => { setShowImportDialog(false); navigate('/add-listing'); }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Edit className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">Manual Entry</p>
                      <p className="text-sm text-gray-500">Fill out the listing form manually</p>
                    </div>
                  </div>
                </Button>
              </div>
            ) : importMethod === 'google' ? (
              <div className="text-center py-4">
                <Globe className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">
                  Google Business Profile API integration requires additional setup.
                </p>
                <Button onClick={handleGoogleImport}>Connect Google Account</Button>
                <Button variant="link" onClick={() => setImportMethod(null)} className="mt-2">
                  Back to options
                </Button>
              </div>
            ) : (
              <div>
                <Label>Google Business URL</Label>
                <Input
                  placeholder="https://www.google.com/maps/place/..."
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Paste the full URL from Google Maps for your business
                </p>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => setImportMethod(null)}>Back</Button>
                  <Button onClick={handleUrlImport} disabled={importing} className="flex-1">
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
